import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Target, Calendar } from "lucide-react";
import { StudentSelector } from "./StudentSelector";

interface TargetingFormProps {
  targeting: any;
  onChange: (targeting: any) => void;
  courses?: { _id: string; title: string }[];
}

export function TargetingForm({
  targeting,
  onChange,
  courses = [],
}: TargetingFormProps) {
  const handleTargetingChange = (field: string, value: any) => {
    console.log("TargetingForm: handleTargetingChange", field, value);
    onChange({
      ...targeting,
      [field]: value,
    });
  };

  const handleNestedChange = (parent: string, field: string, value: any) => {
    onChange({
      ...targeting,
      [parent]: {
        ...targeting[parent],
        [field]: value,
      },
    });
  };

  return (
    <Card className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Target size={20} className="text-[#f09040]" />
        <h3 className="text-lg font-semibold text-[#102040]">
          Audience Targeting
        </h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#102040] mb-2">
            Audience Type
          </label>
          <Select
            value={targeting.audienceType || "all"}
            onChange={(e) => {
              const value = e.target.value;
              let updates: any = { audienceType: value };

              if (value === "all") {
                updates.roles = ["student", "instructor", "admin"];
                updates.specificStudents = [];
              } else if (value !== "custom") {
                // When switching to other types (enrolled, active, etc), clear specificStudents
                updates.specificStudents = [];
              }

              onChange({
                ...targeting,
                ...updates,
              });
            }}
            options={[
              { value: "all", label: "All Students" },
              { value: "enrolled", label: "Enrolled in Courses" },
              { value: "not_enrolled", label: "Not Enrolled in Courses" },
              { value: "active", label: "Active Users" },
              { value: "inactive", label: "Inactive Users" },
              { value: "custom", label: "Custom Query" },
            ]}
          />
        </div>

        {(targeting.audienceType === "enrolled" ||
          targeting.audienceType === "not_enrolled") && (
          <div>
            <label className="block text-sm font-medium text-[#102040] mb-2">
              Select Courses
            </label>
            <div className="border border-gray-200 rounded-lg p-4 min-h-[100px]">
              {courses.length > 0 ? (
                <div className="space-y-2">
                  {courses.map((course) => (
                    <label
                      key={course._id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={(targeting.courses || []).includes(course._id)}
                        onChange={(e) => {
                          const current = targeting.courses || [];
                          if (e.target.checked) {
                            handleTargetingChange("courses", [
                              ...current,
                              course._id,
                            ]);
                          } else {
                            handleTargetingChange(
                              "courses",
                              current.filter((id: string) => id !== course._id),
                            );
                          }
                        }}
                        className="w-4 h-4 text-[#f09040] border-gray-300 rounded focus:ring-[#f09040]"
                      />
                      <span className="text-sm text-[#102040]">
                        {course.title}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#989797]">No courses available</p>
              )}
            </div>
          </div>
        )}

        {targeting.audienceType === "all" && (
          <div className="bg-[#FFF9E6] border border-[#f09040] rounded-lg p-4 flex items-start gap-3">
            <div className="p-2 bg-white rounded-full border border-[#e07828] text-[#e07828] shrink-0">
              <Target size={20} />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[#102040] mb-1">
                Broad Targeting Active
              </h4>
              <p className="text-sm text-[#686868]">
                This campaign will be sent to{" "}
                <strong>all registered students</strong>. Expected audience size
                will be calculated automatically based on current user base.
              </p>
            </div>
          </div>
        )}

        {targeting.audienceType === "custom" && (
          <div>
            <label className="block text-sm font-medium text-[#102040] mb-2">
              Select Specific Users
            </label>
            <StudentSelector
              selectedUserIds={targeting.specificStudents || []}
              onChange={(userIds) =>
                handleTargetingChange("specificStudents", userIds)
              }
            />
          </div>
        )}

        {(targeting.audienceType === "active" ||
          targeting.audienceType === "inactive") && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#102040] mb-2">
                Minimum Days Ago
              </label>
              <Input
                type="number"
                min="0"
                placeholder="e.g., 1"
                value={targeting.lastActivityDays?.min || ""}
                onChange={(e) =>
                  handleNestedChange("lastActivityDays", "min", e.target.value)
                }
                icon={<Calendar size={16} />}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#102040] mb-2">
                Maximum Days Ago
              </label>
              <Input
                type="number"
                min="0"
                placeholder="e.g., 30"
                value={targeting.lastActivityDays?.max || ""}
                onChange={(e) =>
                  handleNestedChange("lastActivityDays", "max", e.target.value)
                }
                icon={<Calendar size={16} />}
              />
            </div>
          </div>
        )}

        {targeting.audienceType !== "all" &&
          targeting.audienceType !== "custom" && (
            <div>
              <label className="block text-sm font-medium text-[#102040] mb-2">
                User Roles
              </label>
              <div className="flex flex-wrap gap-2">
                {["student", "instructor", "admin"].map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      const current = targeting.roles || [];
                      if (current.includes(role)) {
                        handleTargetingChange(
                          "roles",
                          current.filter((r: string) => r !== role),
                        );
                      } else {
                        handleTargetingChange("roles", [...current, role]);
                      }
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                      (targeting.roles || []).includes(role)
                        ? "bg-[#e07828] text-white"
                        : "bg-gray-100 text-[#102040] hover:bg-gray-200"
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
          )}
      </div>
    </Card>
  );
}
